/**
 * Skeleton Loading State Tokens
 * 
 * Source of Truth for all skeleton-related sizes and patterns.
 * 
 * Usage:
 * - Import: import { SKELETONS } from '@/design-system';
 * - Apply: <Skeleton className={SKELETONS.elements.title} />
 * 
 * For complex loading states, use skeleton components from:
 * @/components/skeletons (StatCardSkeleton, TableRowSkeleton, etc.)
 */

export const SKELETONS = {
  // ===========================================
  // ELEMENT TOKENS - For primitive Skeleton usage
  // ===========================================
  elements: {
    // Avatars and icons
    avatar: {
      sm: "h-8 w-8 rounded-full",
      default: "h-10 w-10 rounded-full",
      lg: "h-12 w-12 rounded-full",
      xl: "h-16 w-16 rounded-full",
    },
    icon: {
      sm: "h-4 w-4 rounded",
      default: "h-5 w-5 rounded",
      lg: "h-6 w-6 rounded",
    },
    
    // Text elements
    title: {
      sm: "h-4 w-24",
      default: "h-5 w-32",
      lg: "h-6 w-48",
      xl: "h-8 w-64",
    },
    subtitle: {
      sm: "h-3 w-16",
      default: "h-4 w-24",
      lg: "h-5 w-32",
    },
    paragraph: {
      short: "h-4 w-1/2",
      default: "h-4 w-full",
      multiline: "h-4 w-full", // Use multiple times
    },
    
    // Badges and tags
    badge: {
      sm: "h-4 w-12 rounded-full",
      default: "h-5 w-16 rounded-full",
      lg: "h-6 w-20 rounded-full",
    },
    
    // Buttons
    button: {
      sm: "h-8 w-16 rounded-md",
      default: "h-9 w-24 rounded-md",
      lg: "h-10 w-32 rounded-md",
      icon: "h-9 w-9 rounded-md",
    },
    
    // Inputs
    input: {
      default: "h-9 w-full rounded-md",
      sm: "h-8 w-full rounded-md",
    },
    
    // Images
    image: {
      thumbnail: "h-16 w-16 rounded-md",
      card: "h-32 w-full rounded-t-lg",
      hero: "h-48 w-full rounded-lg",
      heroLg: "h-64 w-full rounded-lg",
      aspectVideo: "aspect-video w-full rounded-lg",
      aspectSquare: "aspect-square w-full rounded-lg",
    },
    
    // Content elements
    content: {
      line: "h-4 w-full",
      lineShort: "h-4 w-3/4",
      lineMedium: "h-4 w-5/6",
    },
    
    // Accordion elements
    accordion: {
      item: "h-14 w-full rounded-lg border px-6 py-4",
      question: "h-5 w-3/4",
      chevron: "h-4 w-4",
    },
    
    // Stat card elements
    stat: {
      value: "h-9 w-16",
      label: "h-4 w-24",
      trend: "h-3 w-20",
    },
    
    // Message/chat elements  
    message: {
      bubble: "h-16 w-full rounded-lg",
      author: "h-4 w-24",
      timestamp: "h-3 w-32",
    },
    
    // Ticket/support elements
    ticket: {
      header: "h-7 w-48",
      status: "h-6 w-20 rounded-full",
      field: "h-5 w-64",
    },
    
    // Filter/search elements
    filter: {
      select: "h-9 w-40 rounded-md",
      button: "h-9 w-24 rounded-md",
      chip: "h-6 w-16 rounded-full",
    },
    
    // Sidebar/menu elements
    menu: {
      item: "h-9 w-full rounded-md",
      icon: "h-4 w-4 rounded",
      label: "h-4 w-24",
    },
    
    // Page header elements (matches TYPOGRAPHY.headerGreeting)
    header: {
      title: "h-6 w-48 sm:h-7 sm:w-56",        // Matches headerGreeting (text-lg sm:text-xl)
      titleLg: "h-7 w-56 sm:h-8 sm:w-64",      // Matches pageTitle (text-2xl sm:text-3xl)
      description: "h-4 w-64 sm:w-80",         // Matches pageDescription
      action: "h-9 w-24 rounded-md",           // Standard button size
    },
    
    // Table elements (for TableRowSkeleton and manual table skeletons)
    table: {
      checkbox: "h-4 w-4",
      headerPrimary: "h-4 w-32",
      headerSecondary: "h-4 w-20",
      headerAction: "h-4 w-16",
      cellPrimary: "h-4 w-3/4",
      cellSecondary: "h-4 w-20",
      cellTertiary: "h-4 w-12",
      cellDescription: "h-4 w-48",
      actionIcon: "h-8 w-8",
      badge: "h-5 w-16 rounded-full",
    },
  },

  // ===========================================
  // COMPONENT TOKENS - Standard component sizes
  // ===========================================
  components: {
    // Cards
    statCard: "h-24 w-full",
    contentCard: "h-48 w-full",
    publicationCard: "h-64 w-full",
    userCard: "h-20 w-full",
    
    // Table
    tableRow: "h-12 w-full",
    tableHeader: "h-10 w-full",
    
    // Forms
    formField: "h-16 w-full",
    formSection: "h-32 w-full",
    
    // Navigation
    navItem: "h-10 w-full",
    tab: "h-9 w-20",
    
    // Charts
    chartCard: "h-64 w-full",
    miniChart: "h-20 w-full",
    
    // Page-level skeletons
    publicationDetail: "min-h-[600px]",
    expertProfile: "min-h-[500px]",
    pageDetail: "min-h-[400px]",
    editor: "min-h-[400px]",
    faqList: "min-h-[300px]",
    
    // Phase 3 - Edge case page skeletons
    ticketDetail: "min-h-[400px]",
    opportunitiesDashboard: "min-h-[600px]",
    myAccount: "min-h-[300px]",
    sidebarMenu: "min-h-[200px]",
    
    // Phase 5 - Final cleanup skeletons
    matrix: "min-h-[400px]",
    coverageMap: "min-h-[300px]",
    
    // Phase 6 - Polish and utilities
    userEditDialog: "min-h-[400px]",
    topicList: "min-h-[300px]",
    publicationTable: "min-h-[200px]",
    
    // Phase 7 - Consolidation
    topicsTable: "min-h-[400px]",
    qualityReportTable: "min-h-[350px]",
    languageOverviewCard: "h-16 w-full",
    expertSearchResult: "h-12 w-full",
    
    // Phase 8 - Dashboard standardization
    analyticsDashboard: "min-h-[500px]",
    adminRolesTable: "min-h-[400px]",
    gatewayCard: "min-h-[300px]",
    
    // Phase 9 - Final consolidation
    healthCheckTab: "min-h-[500px]",
    searchAnalyticsTab: "min-h-[400px]",
    costTrackingTab: "min-h-[350px]",
    faqPerformanceTable: "min-h-[250px]",
    
    // Phase 10 - Public pages and admin cleanup
    topicCardGrid: "min-h-[300px]",
    localizationManager: "min-h-[250px]",
    accountAnalytics: "min-h-[400px]",
    topicRelationships: "min-h-[350px]",
    pageSpeedTab: "min-h-[450px]",
    
    // Phase 11 additions
    abTestManager: "min-h-[400px]",
    revisionHistoryPanel: "min-h-[250px]",
    emailBrandingTable: "min-h-[150px]",
    documentContent: "min-h-[120px]",
    
    // Phase 12 additions - Intelligence tab
    topicDiscoveryTable: "min-h-[300px]",
    topicLifecycleTable: "min-h-[350px]",
    dataSourceVerification: "min-h-[400px]",
    contentReviewQueue: "min-h-[300px]",
    
    // Phase 13 additions - Button loading states audit
    topicSuggestions: "min-h-[48px]",
    
    // Unified page skeletons
    accountPage: "min-h-[400px]",
  },

  // ===========================================
  // CONTAINER TOKENS - Wrapper sizes
  // ===========================================
  container: {
    compact: "h-40",
    default: "h-64",
    lg: "h-96",
    full: "h-full w-full",
  },

  // ===========================================
  // CHART TOKENS - For chart components
  // ===========================================
  chart: {
    sm: "h-[200px] w-full",
    default: "h-64 w-full",
    lg: "h-[400px] w-full",
  },

  // ===========================================
  // LEGACY TOKENS - Backward compatibility
  // ===========================================
  metric: "h-32",
  card: "h-24",
  row: "h-12",
  table: "h-80",
  banner: "h-20",
} as const;

// Type exports for strict typing
export type SkeletonElementToken = keyof typeof SKELETONS.elements;
export type SkeletonComponentToken = keyof typeof SKELETONS.components;
export type SkeletonContainerToken = keyof typeof SKELETONS.container;
export type SkeletonChartToken = keyof typeof SKELETONS.chart;
